import React, { useContext, useRef, useEffect, useState, useMemo } from "react";
import { Box, Container, Typography, useMediaQuery, useTheme, Button, Tooltip } from '@mui/material';
import { BusinessContext } from '../context/BusinessContext';
import { ReviewsContext } from '../context/ReviewsContext';
import ReviewsTable from '../components/ReviewsTable';
import ReviewsOverview from '../components/ReviewsOverview';
import CumulativeRatingChart from '../components/CumulativeRatingChart';

import RateReviewIcon from '@mui/icons-material/RateReview';
import TimelineIcon from '@mui/icons-material/Timeline';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate, useLocation } from 'react-router-dom';
import Rating from '@mui/material/Rating';
import { SortOptionContext } from '../context/SortOptionProvider';
import { spacing } from '../utils/themeUtils';

const Reviews = () => {
  const { currencySymbol, selectedBusinesses } = useContext(BusinessContext);
  const { 
    reviewsData,
    view, 
    selectedCategory,
    setSelectedCategory,
    selectedSubject,
    setSelectedSubject,
    setTimeframe,
    resetFilters,
    selectedMonthRange,
    setSelectedMonthRange,
    triggerRefetch,
    isLoading,
    error,
    selectedReviewId,
    setSelectedReviewId
  } = useContext(ReviewsContext);
  const theme = useTheme();
  const isLandscape = useMediaQuery('(orientation: landscape) and (max-width: 900px)');
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [signupDate, setSignupDate] = useState(null);
  const formattedDate = signupDate ? signupDate.toLocaleDateString() : '';
  const navigate = useNavigate();
  const [subscriptionActive, setSubscriptionActive] = useState(null);
  const location = useLocation();
  const [isSubscribed, setIsSubscribed] = useState(true);

  const chartsContainerRef = useRef(null);
  const reviewsTableContainerRef = useRef(null);

  const numSelected = selectedBusinesses?.length || 0;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const monthParam = params.get('month');
    const categoryParam = params.get('category');
    const subjectGroupParam = params.get('subject_group');
    const timeframeParam = params.get('timeframe');
    const reviewIdParam = params.get('review_id');

    if (reviewIdParam) {
      if (typeof setSelectedReviewId === 'function' && reviewIdParam !== selectedReviewId) {
        setSelectedReviewId(reviewIdParam);
      }
    } else {
      if (typeof setSelectedReviewId === 'function' && selectedReviewId !== null) {
        setSelectedReviewId(null);
      }
    }

    if (monthParam) {
      const [year, month] = monthParam.split('-').map(Number);
      if (year && month && !isNaN(year) && !isNaN(month)) {
        const startDate = new Date(year, month - 1, 1);
        const endDate = new Date(year, month, 0);
        if (JSON.stringify(selectedMonthRange) !== JSON.stringify({ start: startDate, end: endDate })) {
            if (typeof setSelectedMonthRange === 'function') setSelectedMonthRange({ start: startDate, end: endDate });
        }
      } else {
        if (selectedMonthRange !== null && typeof setSelectedMonthRange === 'function') setSelectedMonthRange(null);
      }
    } else {
      if (!timeframeParam && selectedMonthRange !== null && typeof setSelectedMonthRange === 'function') {
        // setSelectedMonthRange(null); // Current behavior: month sticks if timeframe not 'all' and no month param
      }
    }
    
    if (typeof setSelectedCategory === 'function') {
      if (selectedCategory !== (categoryParam || null)) setSelectedCategory(categoryParam || null);
    }
    if (typeof setSelectedSubject === 'function') { 
      if (selectedSubject !== (subjectGroupParam || null)) setSelectedSubject(subjectGroupParam || null);
    }
    
    if (timeframeParam && typeof setTimeframe === 'function') {
      setTimeframe(timeframeParam);
    } else if (!monthParam && !timeframeParam) {
      // If no month and no timeframe from URL, context timeframe (from localStorage or default) persists
      // Or set to a default if desired: setTimeframe(DEFAULT_TIMEFRAME_VALUE);
    }

  }, [location.search, selectedReviewId, selectedMonthRange, selectedCategory, selectedSubject, setSelectedReviewId, setSelectedMonthRange, setSelectedCategory, setSelectedSubject, setTimeframe]);

  useEffect(() => {
    const filtersActive = selectedCategory || selectedSubject || selectedMonthRange || selectedReviewId;
    if (!filtersActive) return;

    const handlePageClick = (event) => {
      const isOutsideChart = !(chartsContainerRef.current && chartsContainerRef.current.contains(event.target));
      const isFilterExempt = event.target.closest('[data-filter-exempt="true"]');
      const isInsideReviewsTable = reviewsTableContainerRef.current && reviewsTableContainerRef.current.contains(event.target);
      const isSidebarNavigation = 
        !!event.target.closest('.MuiDrawer-root') ||
        !!event.target.closest('.MuiDrawer-paper') ||
        !!event.target.closest('[role="presentation"]') ||
        event.target.closest('[data-testid="navigation-menu"]');

      if (isOutsideChart && !isFilterExempt && !isInsideReviewsTable && !isSidebarNavigation) {
        if (typeof resetFilters === 'function') {
            resetFilters();
        }
        navigate('/reviews', { replace: true });
      }
    };

    document.addEventListener('click', handlePageClick);

    return () => {
      document.removeEventListener('click', handlePageClick);
    };
  }, [selectedCategory, selectedSubject, selectedMonthRange, selectedReviewId, resetFilters, navigate, chartsContainerRef, reviewsTableContainerRef, setSelectedCategory, setSelectedSubject, setSelectedMonthRange, setSelectedReviewId]);

  const handleUnlockOngoingAnalysis = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/payment-checkout`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ plan: 'monthly' }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }

      const { checkout_url } = await response.json();
      window.location.href = checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
    }
  };

  return (
    <Box 
      sx={{
        minHeight: 'auto',
        background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
        display: 'flex',
        flexDirection: 'column',
        pt: spacing.pageTop,
        pb: spacing.pageBottom,
        px: spacing.pageHorizontal,
        opacity: 0,
        animation: 'fadeIn 1s forwards',
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Container 
        maxWidth={false}
        sx={{
          width: '100%',
          pb: { 
            xs: 6,
            md: 8,
            lg: 10 
          }
        }}
      >
        {/* Mobile Premium Banner removed - now only on Businesses page */}
        
        {/* Header Section with 3-way split */}
        <Box sx={{ 
          mb: { 
            xs: 2,
            lg: 4 
          },
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: { xs: 2, md: 3 },
          alignItems: { xs: 'flex-start', md: 'flex-start' },
          width: '100%',
          height: { xs: 'auto', md: '280px' }
        }}>
          {/* Title Section - 1/3 width */}
          <Box sx={{ 
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: spacing.headerHorizontal,
            height: { xs: 'auto', md: '100%' }
          }}>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { 
                  xs: '1.75rem',
                  lg: '2.5rem'
                },
                color: '#0A2540',
                letterSpacing: '-0.02em',
                mb: { 
                  xs: 1,
                  lg: 2 
                },
                display: 'flex',
                alignItems: 'center',
                gap: { 
                  xs: 1,
                  lg: 2 
                }
              }}
            >
              <RateReviewIcon sx={{ 
                fontSize: 'inherit',
                color: '#1976d2'
              }} />
              Reviews
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#546E7A',
                fontSize: { 
                  xs: '0.95rem',
                  lg: '1.1rem'
                }
              }}
            >
              Analyze your customer reviews and their impact on your business
            </Typography>
          </Box>
          
          {/* Reviews Overview Section - 1/3 width */}
          <Box sx={{ 
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: spacing.headerHorizontal,
            height: { xs: 'auto', md: '100%' }
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <InfoIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography 
                variant="h6" 
                fontWeight="bold"
                sx={{ 
                  borderBottom: '2px solid #1976d2',
                  pb: 0.5,
                  display: 'inline-block',
                  fontSize: '1.25rem'
                }}
              >
                Overview
              </Typography>
            </Box>
            <Box sx={{ height: { xs: 'auto', md: 'calc(100% - 40px)' }, overflow: 'auto' }}>
              <ReviewsOverview />
            </Box>
          </Box>
          
          {/* Rating Chart Section - 1/3 width */}
          <Box sx={{ 
            flex: { xs: '1 1 100%', md: '1 1 33.33%' },
            px: spacing.headerHorizontal,
            height: { xs: 'auto', md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            width: '100%'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, visibility: numSelected === 0 ? 'hidden' : 'visible' }}>
              <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography
                variant="h6" 
                fontWeight="bold"
                sx={{ 
                  borderBottom: '2px solid #1976d2',
                  pb: 0.5,
                  display: 'inline-block',
                  fontSize: '1.25rem',
                }}
              >
                Rating History
              </Typography>
            </Box>
            <Box 
              ref={chartsContainerRef}
              sx={{ 
                flex: 1,
                height: { xs: 'auto', md: 'calc(100% - 40px)' },
                minHeight: { xs: '250px', md: 'auto' },
                maxHeight: { xs: '350px', md: 'calc(100% - 40px)' },
                display: 'flex',
                position: 'relative',
                overflow: 'hidden',
                width: '100%',
                '& svg': {
                  overflow: 'hidden',
                  maxHeight: '350px !important' 
                },
                '& .MuiAreaElement-series-ratingTrend': {
                  display: 'none'
                }
              }}
            >
              <CumulativeRatingChart />
            </Box>
          </Box>
        </Box>

        <Box ref={reviewsTableContainerRef} sx={{ mt: 3, alignItems: 'baseline', position: 'relative' }}>
          <ReviewsTable
            currencySymbol={currencySymbol}
            view={view}
            highlightCategory={selectedCategory}
            highlightSubject={selectedSubject}
          />
        </Box>
      </Container>
    </Box>
  );
};

export default Reviews;
